
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Badge,
  Box,
  Typography,
  Button,
  CircularProgress,
  Pagination,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { ActivityList, ActivityFiltersPanel } from './components';
import { usePagination } from './usePagination';
import { useActivityFilters } from './useActivityFilters';
import { useActivityList } from './useActivityList';
import { activityService } from '../../services/activityService';
import type { ValidationErrorResponse } from '../../types/activity';
import { clearCreateActivityDraft } from '../../utils/activityDraftStorage';
import { loadActivityPage, saveActivityPage } from '../../utils/activityFiltersStorage';

// Keyed by company so that filter/page state never leaks between companies when only the route param changes
export default function ActivityPage() {
  const { id } = useParams<{ id: string }>();
  return <ActivityPageContent key={id} />;
}

function ActivityPageContent() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const companyId = id ? parseInt(id) : null;

  // Delete confirmation state
  const [deleteGroupId, setDeleteGroupId] = useState<number | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [restoreError, setRestoreError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Restore the last viewed page; useActivityFilters resets it if the filters differ from the saved ones
  const [restoredPage] = useState(() => (companyId ? loadActivityPage(companyId) : 0));

  // Pagination
  const {
    data: { page, pageSize, totalPages },
    actions: { setPage, setTotalPages, resetToFirstPage, handlePageChange },
  } = usePagination({ initialPage: restoredPage, pageSize: 10 });

  useEffect(() => {
    if (companyId) {
      saveActivityPage(companyId, page);
    }
  }, [companyId, page]);

  // Filters
  const {
    filterData,
    filterState,
    filterActions,
  } = useActivityFilters({
    companyId,
    onFiltersChange: resetToFirstPage,
  });

  // Activities data
  const { data: { groupItems, loading, error } } = useActivityList({
    companyId,
    page,
    pageSize,
    filterData,
    onPageCountChange: setTotalPages,
    refreshKey,
  });

  // The restored page may no longer exist (e.g. items were archived meanwhile)
  useEffect(() => {
    if (!loading && !error && page > 0 && page >= totalPages) {
      setPage(Math.max(totalPages - 1, 0));
    }
  }, [loading, error, totalPages, page, setPage]);

  const handleAddActivity = () => {
    if (companyId === null) return;

    clearCreateActivityDraft(companyId);
    navigate(`/companies/${companyId}/activities/create`);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteGroupId(null);
    setDeleteError(null);
  };

  const handleDeleteGroup = async () => {
    if (!deleteGroupId) return;
    setDeleteLoading(true);
    setDeleteError(null);
    try {
      await activityService.deleteActivityGroup(deleteGroupId);
      setRefreshKey((k) => k + 1);
      setDeleteGroupId(null);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number; data?: ValidationErrorResponse } };
      if (axiosErr?.response?.status === 422) {
        const data = axiosErr.response.data;
        const msg = data?.generalErrors?.[0] ?? 'Не можна видалити: помилка валідації балансу.';
        setDeleteError(msg);
      } else {
        setDeleteError('Не вдалося видалити групу активностей.');
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleRestoreGroup = async (groupId: number) => {
    setRestoreError(null);
    try {
      await activityService.restoreActivityGroup(groupId);
      setRefreshKey((k) => k + 1);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number; data?: ValidationErrorResponse } };
      if (axiosErr?.response?.status === 422) {
        const data = axiosErr.response.data;
        setRestoreError(data?.generalErrors?.[0] ?? 'Не можна відновити: помилка валідації балансу.');
      } else {
        setRestoreError('Не вдалося відновити групу активностей.');
      }
    }
  };

  if (!companyId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Некоректний ID компанії
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      <Box
        sx={{
          position: 'sticky',
          top: 64,
          zIndex: 10,
          backgroundColor: 'background.default',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          py: 1,
          mb: 0.5,
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/companies/${companyId}/home`)}
          size="small"
        >
          На головну
        </Button>
        <Typography
          variant="button"
          component="h1"
          noWrap
          sx={{
            minWidth: 0,
            pr: { xs: '9px', sm: '10px' },
            color: 'primary.main',
          }}
        >
          Активності
        </Typography>
      </Box>

      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: { xs: 1, sm: 2 },
            pt: 1,
          }}
        >
          <Tooltip title="Показати/сховати фільтри">
            <IconButton
              size="small"
              aria-label="Показати/сховати фільтри"
              onClick={() => filterActions.setShowFilters(!filterState.showFilters)}
              color={filterState.showFilters || filterState.activeFiltersCount > 0 ? 'primary' : 'default'}
            >
              <Badge
                badgeContent={filterState.activeFiltersCount}
                color="primary"
              >
                <SearchIcon />
              </Badge>
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddActivity}
          >
            Додати
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <ActivityFiltersPanel
        show={filterState.showFilters}
        filterData={filterData}
        filterActions={filterActions}
      />

      {filterState.typesError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {filterState.typesError}
        </Alert>
      )}

      {restoreError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setRestoreError(null)}>
          {restoreError}
        </Alert>
      )}

      {/* Activity List */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      ) : (
        <>
          <ActivityList
            groupItems={groupItems}
            companyId={companyId!}
            onDeleteGroup={(groupId) => setDeleteGroupId(groupId)}
            onRestoreGroup={handleRestoreGroup}
          />
          
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={totalPages}
                page={page + 1}
                onChange={handlePageChange}
                color="primary"
              />
            </Box>
          )}
        </>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteGroupId !== null} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Видалити групу активностей?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Це заархівує групу: всі її активності перестануть впливати на баланси складів. Групу можна буде
            відновити пізніше.
          </DialogContentText>
          {deleteError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {deleteError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} disabled={deleteLoading}>
            Скасувати
          </Button>
          <Button onClick={handleDeleteGroup} color="error" disabled={deleteLoading}>
            {deleteLoading ? 'Видалення...' : 'Видалити'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
